import type React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { Megaphone, Send, AlertTriangle, Clock } from "lucide-react";
import axios from "axios";
import { getAuthHeader } from "../../utils/authHeader";
import { AnimatePresence } from "framer-motion";
import { useIsMobile } from "../../hooks/useIsMobile";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { AnnouncementHistory } from "./types";
import History from "./History";
import AddNotification from "./AddNotification";

const AdminAnnouncementsPage: React.FC = () => {
    const isMobile = useIsMobile(900);
    const [isReconfirmOpen, setIsReconfirmOpen] = useState(false);

    const [page, setPage] = useState(1);
    const limit = 10;
    const [total, setTotal] = useState(0);
    const [type, setType] = useState<"announcement">("announcement");
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [url, setUrl] = useState("");
    const [expiryDate, setExpiryDate] = useState<Date | null>(null);
    const [expiryHour, setExpiryHour] = useState(12);
    const [expiryMinute, setExpiryMinute] = useState(0);
    const [expiryPeriod, setExpiryPeriod] = useState<"AM" | "PM">("PM");
    const [activeMessageTab, setActiveMessageTab] = useState<
        "write" | "preview"
    >("write");

    const handleHourChange = (val: string) => setExpiryHour(Number(val));
    const handleMinuteChange = (val: string) => setExpiryMinute(Number(val));
    const handlePeriodChange = (val: string) =>
        setExpiryPeriod(val as "AM" | "PM");
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [feedback, setFeedback] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);
    const [history, setHistory] = useState<AnnouncementHistory[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState<"compose" | "history">(
        "compose"
    );
    const [filterType, setFilterType] = useState<string | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const expandedRef = useRef<HTMLTableRowElement | null>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                expandedRef.current &&
                !expandedRef.current.contains(event.target as Node)
            ) {
                setExpandedId(null);
            }
        }
        if (expandedId) {
            document.addEventListener("mousedown", handleClickOutside);
            return () =>
                document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [expandedId]);

    const loadAnnouncementHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const resp = await axios.get<{
                data: AnnouncementHistory[];
                total: number;
            }>(
                `${process.env.REACT_APP_BACKEND_URL}/notifications/announcements`,
                {
                    params: { page, limit },
                    headers: await getAuthHeader(),
                }
            );
            setHistory(resp.data.data);
            setTotal(resp.data.total);
        } catch (err) {
            console.error(err);
        } finally {
            setHistoryLoading(false);
        }
    }, [page, limit]);

    useEffect(() => {
        loadAnnouncementHistory();
    }, [loadAnnouncementHistory]);

    // Fix for issue #2: Create a safe date setter that won't affect other form fields
    const handleExpiryDateChange = (date: Date | null) => {
        setExpiryDate(date);
    };

    const handleSubmit = async () => {
        setIsSending(true);
        setFeedback(null);

        try {
            let expiryIso: string | undefined;
            if (expiryDate) {
                // convert 12h → 24h
                let h24 = expiryHour % 12;
                if (expiryPeriod === "PM") h24 += 12;
                const dt = new Date(expiryDate);
                dt.setHours(h24, expiryMinute, 0, 0);

                // block if in the past
                if (dt.getTime() < Date.now()) {
                    toast.error(
                        <CustomToast
                            message="Expiry must be in the future"
                            type="error"
                        />
                    );
                    setIsSending(false);
                    return;
                }

                expiryIso = dt.toISOString();
            }

            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/notifications/create-announcement`,
                { type, title, message, url, expiryDate: expiryIso },
                { headers: await getAuthHeader() }
            );

            setFeedback({
                type: "success",
                message: "Announcement sent to all users",
            });
            setTitle("");
            setMessage("");
            setUrl("");
            loadAnnouncementHistory();
        } catch (err) {
            console.error(err);
            setFeedback({
                type: "error",
                message: "Failed to send announcement",
            });
        } finally {
            setIsSending(false);
        }
    };

    const filteredHistory = history
        .filter(
            (a) =>
                (search === "" ||
                    a.title.toLowerCase().includes(search.toLowerCase()) ||
                    a.message.toLowerCase().includes(search.toLowerCase())) &&
                (filterType === null || a.type === filterType)
        )
        .sort(
            (a, b) =>
                new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
        );

    const getTypeIcon = (announcementType: string) => {
        switch (announcementType) {
            case "announcement":
                return <AlertTriangle className="w-5 h-5 text-amber-500" />;
            default:
                return <AlertTriangle className="w-5 h-5 text-amber-500" />;
        }
    };

    const getTypeColor = (announcementType: string) => {
        switch (announcementType) {
            case "announcement":
                return "bg-amber-100 text-amber-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const res = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/notifications/announcements/${id}`,
                {
                    headers: await getAuthHeader(),
                }
            );
            setHistory(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error(err);
            alert("Failed to delete announcement");
        }
    };

    const cancelDelete = () => {
        setIsReconfirmOpen(false);
        setDeleteId(null);
    };

    const confirmDelete = async () => {
        setIsReconfirmOpen(false);
        if (!deleteId) return;
        await handleDelete(deleteId);
        setDeleteId(null);
    };

    // Markdown formatting helpers
    const insertMarkdownFormat = (format: string) => {
        const textarea = document.getElementById(
            "message"
        ) as HTMLTextAreaElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = textarea.value.substring(start, end);

        let formattedText = "";
        switch (format) {
            case "bold":
                formattedText = `**${selectedText || "bold text"}**`;
                break;
            case "italic":
                formattedText = `*${selectedText || "italic text"}*`;
                break;
            case "bullet":
                formattedText = `\n- ${selectedText || "list item"}`;
                break;
            case "numbered":
                formattedText = `\n1. ${selectedText || "list item"}`;
                break;
            case "heading":
                formattedText = `\n## ${selectedText || "Heading"}`;
                break;
            case "link":
                formattedText = `[${selectedText || "link text"}](url)`;
                break;
            case "linebreak":
                formattedText = "\n\n";
                break;
            default:
                formattedText = selectedText;
        }

        const newValue =
            textarea.value.substring(0, start) +
            formattedText +
            textarea.value.substring(end);
        setMessage(newValue);

        // Set cursor position after the inserted text
        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + formattedText.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            {/* Header with Gradient */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-lightSecondary to-secondary p-8 shadow-lg mb-8">
                <div className="absolute top-0 right-0 opacity-10">
                    <Megaphone className="w-64 h-64 -mt-12 -mr-12 text-primary" />
                </div>
                <div className="relative z-10">
                    <h1 className="text-3xl font-bold text-primary flex items-center mb-2">
                        <Megaphone className="w-8 h-8 mr-3" />
                        Announcements Dashboard
                    </h1>
                    <p className="text-primary text-lg max-w-2xl">
                        Create and manage system-wide announcements to keep your
                        users informed and engaged
                    </p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    onClick={() => setActiveTab("compose")}
                    className={`px-6 py-3 font-medium text-sm flex items-center ${
                        activeTab === "compose"
                            ? "text-secondary border-b-2 border-secondary"
                            : "text-primary hover:text-secondary"
                    }`}
                >
                    <Send className="w-4 h-4 mr-2" />
                    Compose
                </button>
                <button
                    onClick={() => setActiveTab("history")}
                    className={`px-6 py-3 font-medium text-sm flex items-center ${
                        activeTab === "history"
                            ? "text-secondary border-b-2 border-secondary"
                            : "text-primary hover:text-secondary"
                    }`}
                >
                    <Clock className="w-4 h-4 mr-2" />
                    History
                </button>
            </div>

            <AnimatePresence mode="wait">
                {activeTab === "compose" ? (
                    <AddNotification
                        feedback={feedback}
                        setFeedback={setFeedback}
                        title={title}
                        setTitle={setTitle}
                        message={message}
                        setMessage={setMessage}
                        type={type}
                        setType={setType}
                        isMobile={isMobile}
                        isCalendarOpen={isCalendarOpen}
                        setIsCalendarOpen={setIsCalendarOpen}
                        expiryDate={expiryDate}
                        expiryHour={expiryHour}
                        expiryMinute={expiryMinute}
                        expiryPeriod={expiryPeriod}
                        isSending={isSending}
                        handleSubmit={handleSubmit}
                        handleExpiryDateChange={handleExpiryDateChange}
                        handleMinuteChange={handleMinuteChange}
                        handlePeriodChange={handlePeriodChange}
                        insertMarkdownFormat={insertMarkdownFormat}
                        activeMessageTab={activeMessageTab}
                        setActiveMessageTab={setActiveMessageTab}
                        handleHourChange={handleHourChange}
                        setExpiryDate={setExpiryDate}
                        setExpiryHour={setExpiryHour}
                        setExpiryMinute={setExpiryMinute}
                        setExpiryPeriod={setExpiryPeriod}
                        setIsSending={setIsSending}
                    />
                ) : (
                    <History
                        activeTab={activeTab}
                        search={search}
                        setSearch={setSearch}
                        isFilterOpen={isFilterOpen}
                        setIsFilterOpen={setIsFilterOpen}
                        filterType={filterType}
                        setFilterType={setFilterType}
                        getTypeIcon={getTypeIcon}
                        historyLoading={historyLoading}
                        filteredHistory={filteredHistory}
                        setExpandedId={setExpandedId}
                        expandedId={expandedId}
                        setDeleteId={setDeleteId}
                        setIsReconfirmOpen={setIsReconfirmOpen}
                        cancelDelete={cancelDelete}
                        confirmDelete={confirmDelete}
                        getTypeColor={getTypeColor}
                        history={history}
                        total={total}
                        page={page}
                        setPage={setPage}
                        limit={limit}
                        isReconfirmOpen={isReconfirmOpen}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminAnnouncementsPage;
